import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DrawerFooter } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AgentRole, AgentStatus, type Agent, type Team } from "@/types/team";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import BotMcpTokens from "./BotMcpTokens";

interface AgentFormProps {
  agent: Partial<Agent>;
  teamsSended: Team[];
  setAgent: (agent: Partial<Agent>) => void;
  onSave: () => void;
  onCancel: () => void;
  isCreate?: boolean;
  isLoading?: boolean;
  /** Só no modo edição — troca a senha do agente (via admin), opcional. */
  newPassword?: string;
  onNewPasswordChange?: (value: string) => void;
}

export default function AgentForm({
  agent,
  teamsSended,
  setAgent,
  onSave,
  onCancel,
  isCreate = false,
  isLoading = false,
  newPassword = "",
  onNewPasswordChange,
}: AgentFormProps) {
  const [selectedTeams, setSelectedTeams] = useState<string[]>(agent.teams || []);
  const [selectedPermissions, setSelectedPermissions] = useState<AgentRole[]>(
    agent.permissions?.length ? [agent.permissions[0]] : [AgentRole.APP_AGENT]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAgent({ ...agent, [name]: value });
  };

  const handleTeamSelect = (teamId: string) => {
    if (selectedTeams.includes(teamId)) {
      const newTeams = selectedTeams.filter((id) => id !== teamId);
      setSelectedTeams(newTeams);
      setAgent({ ...agent, teams: newTeams });
    } else {
      const newTeams = [...selectedTeams, teamId];
      setSelectedTeams(newTeams);
      setAgent({ ...agent, teams: newTeams });
    }
  };

  const handlePermissionSelect = (permission: AgentRole) => {
    if (selectedPermissions.includes(permission)) {
      return;
    }

    const newPermissions = [permission];
    setSelectedPermissions(newPermissions);
    setAgent({ ...agent, permissions: newPermissions });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="grid items-center gap-4">
          <Label htmlFor="name">Nome</Label>
          <Input
            id="name"
            name="name"
            value={agent.name || ""}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="grid items-center gap-4">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={agent.email || ""}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        {!agent.isBot && (
          <div className="grid items-center gap-4">
            <Label htmlFor="phone">Telefone</Label>
            <Input
              id="phone"
              name="phone"
              value={agent.phone || ""}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>
        )}

        <div className="grid items-center gap-4">
          <Label>Equipes</Label>
          <div className="flex flex-wrap gap-2">
            {teamsSended.map((team) => (
              <Badge
                key={team._id}
                variant={selectedTeams.includes(team._id) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => handleTeamSelect(team._id)}
              >
                {team.name}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid items-center gap-4">
          <Label>Permissões</Label>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={selectedPermissions.includes(AgentRole.APP_ADMIN) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => handlePermissionSelect(AgentRole.APP_ADMIN)}
            >
              Administrador
            </Badge>
            <Badge
              variant={selectedPermissions.includes(AgentRole.APP_AGENT) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => handlePermissionSelect(AgentRole.APP_AGENT)}
            >
              Agente
            </Badge>
          </div>
        </div>

        {!isCreate && !agent.isBot && onNewPasswordChange && (
          <div className="grid items-center gap-4">
            <Label htmlFor="newPassword">Nova senha</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              placeholder="Deixe em branco pra manter a senha atual"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {newPassword.length > 0 && newPassword.length < 6 && (
              <p className="text-sm text-destructive">A senha precisa ter pelo menos 6 caracteres.</p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="active">Ativo</Label>
          <Switch
            id="active"
            checked={agent.status === AgentStatus.ACTIVE}
            onCheckedChange={(checked) =>
              setAgent({
                ...agent,
                status: checked
                  ? AgentStatus.ACTIVE
                  : AgentStatus.INACTIVE,
              })
            }
            disabled={isLoading}
          />
        </div>

        {!isCreate && agent.isBot && agent._id && <BotMcpTokens agentId={agent._id} />}
      </div>

      <DrawerFooter>
        <Button
          onClick={onSave}
          disabled={
            isLoading ||
            !agent.name ||
            !agent.email ||
            (newPassword.length > 0 && newPassword.length < 6)
          }
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isCreate ? "Criando..." : "Salvando..."}
            </>
          ) : isCreate ? (
            agent.isBot ? "Criar Bot" : "Criar Agente"
          ) : (
            "Salvar Alterações"
          )}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
      </DrawerFooter>
    </>
  );
}
